import LegalLayout, { Section } from "@/components/LegalLayout";

const TOC = [
  { id: "overview", label: "Overview" },
  { id: "subscriptions", label: "Subscription Services" },
  { id: "cooling-off", label: "EU/UK Cooling-Off" },
  { id: "request", label: "Refund Requests" },
  { id: "non-refundable", label: "Non-Refundable Situations" },
  { id: "chargebacks", label: "Chargebacks" },
  { id: "paddle", label: "Paddle Processing" },
  { id: "consumer", label: "Consumer Rights" },
  { id: "contact", label: "Contact" },
];

export default function Refund() {
  return (
    <LegalLayout
      title="Refund Policy"
      effectiveDate="12 June 2026"
      lastUpdated="12 June 2026"
      tableOfContents={TOC}
      testid="refund-page"
    >
      <div className="bg-card border border-border rounded-2xl p-5 sm:p-6 text-sm text-muted-foreground leading-relaxed">
        This Refund Policy explains when and how refunds may be issued for subscriptions and one-time purchases made through the
        ORIXA Intelligence platform. It forms an integral part of our <a href="/legal/terms" className="text-primary underline underline-offset-4">Terms of Service</a>.
      </div>

      <Section id="overview" number="01" title="Overview">
        <p>All paid plans on ORIXA Intelligence (Starter, Professional, Enterprise) and any one-time purchases are sold and billed through <strong className="text-foreground">Paddle.com Market Limited</strong> acting as Merchant of Record. This Policy describes our standard refund practice; consumer protection rights granted by your local law continue to apply where they offer greater protection.</p>
      </Section>

      <Section id="subscriptions" number="02" title="Subscription Services">
        <ul>
          <li>You may cancel your subscription at any time from your workspace settings;</li>
          <li>Cancellation prevents future billing cycles but does not automatically refund the current period;</li>
          <li>After cancellation, you retain access to the Services until the end of the current paid period;</li>
          <li>Annual plans are billed up front; refunds for unused months are generally not provided except as expressly required by law or approved on a case-by-case basis under section 4.</li>
        </ul>
      </Section>

      <Section id="cooling-off" number="03" title="EU/UK 14-Day Cooling-Off Right">
        <p>If you are a consumer (and not a business) resident in the European Economic Area or the United Kingdom, you have a statutory right to withdraw from the purchase of digital services within fourteen (14) days of purchase, provided you have not actively used the Service in a material way during that period.</p>
        <p>By starting to use the Services (for example, by creating an assessment that triggers our AI agent pipeline) within the cooling-off period, you expressly consent to immediate performance and acknowledge that you lose your right of withdrawal once performance has begun.</p>
      </Section>

      <Section id="request" number="04" title="Refund Requests">
        <p>Refund requests will be evaluated on a case-by-case basis. Reasonable grounds that may justify a full or partial refund include:</p>
        <ul>
          <li><strong className="text-foreground">Duplicate charges</strong> &mdash; you were billed twice for the same period;</li>
          <li><strong className="text-foreground">Technical failures</strong> &mdash; a material defect prevented you from accessing the Services for an extended period and could not be remedied;</li>
          <li><strong className="text-foreground">Billing errors</strong> &mdash; incorrect plan, currency or tax;</li>
          <li><strong className="text-foreground">Unauthorised use</strong> &mdash; charges resulting from compromised credentials, subject to identity verification;</li>
          <li><strong className="text-foreground">Applicable consumer law</strong> &mdash; refunds required by mandatory provisions of your local consumer protection legislation.</li>
        </ul>
        <p>To submit a request, please contact us within thirty (30) days of the disputed charge through the channels listed in section 9, providing your account email, transaction reference and a description of the issue.</p>
      </Section>

      <Section id="non-refundable" number="05" title="Non-Refundable Situations">
        <p>Refunds are generally <strong className="text-foreground">not</strong> available in the following cases:</p>
        <ul>
          <li>Change of mind after the cooling-off period has elapsed or after material use of the Services;</li>
          <li>Partial usage of a subscription period;</li>
          <li>Failure to use purchased Services or to log in during a billing period;</li>
          <li>Dissatisfaction with strategic, investment or business outcomes derived from platform outputs &mdash; the Services provide intelligence and decision-support content only;</li>
          <li>Account suspension or termination for breach of our <a href="/legal/terms" className="text-primary underline underline-offset-4">Terms of Service</a>.</li>
        </ul>
      </Section>

      <Section id="chargebacks" number="06" title="Chargebacks">
        <p>Before initiating a chargeback or payment dispute with your bank, please first contact us so we can investigate and, where appropriate, resolve the issue directly. Chargebacks filed without prior contact, or determined to be fraudulent or abusive, may result in immediate suspension or termination of your account and the recovery of any associated costs.</p>
      </Section>

      <Section id="paddle" number="07" title="Paddle Processing">
        <p>Approved refunds are issued through Paddle, using the original payment method, and are subject to Paddle&apos;s processing timelines (typically 5&ndash;10 business days, depending on your bank or card issuer). Currency conversion fees, where applicable, are governed by your card issuer&apos;s policies.</p>
      </Section>

      <Section id="consumer" number="08" title="Consumer Rights">
        <p>Nothing in this Refund Policy limits or excludes any non-waivable statutory rights granted to you by applicable consumer protection laws in your country of residence.</p>
      </Section>

      <Section id="contact" number="09" title="Contact">
        <p>Refund requests should be submitted via the <a href="/book" className="text-primary underline underline-offset-4">contact page</a> or through the contact channels published in your organisation&apos;s workspace. Please include your account email, the transaction reference and a clear description of the issue.</p>
      </Section>
    </LegalLayout>
  );
}
